import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Briefcase, Plus, X, UserPlus, MapPin, DollarSign, Mail, Phone, Clock, ShieldCheck, FileSignature, CheckCircle2, ArrowRight, XCircle, Wrench, Key, Trash2, Sparkles, IdCard, AlertTriangle } from "lucide-react";

function ChipsRow({ label, items, options, onRemove, onAdd, testid }) {
  const { t } = useI18n();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const remaining = (options || []).filter((opt) => !(items || []).includes(opt));
  const addCustom = () => {
    if (!custom.trim()) return;
    if (!(items || []).includes(custom.trim())) onAdd?.(custom.trim());
    setCustom("");
  };
  return (
    <div data-testid={testid}>
      <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold mb-1">{label}</div>
      <div className="flex flex-wrap gap-1 items-center">
        {(items || []).map((it) => (
          <span key={it} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] bg-[#e0e7ff] text-[#4338ca] text-[10.5px]">
            {it}
            <button type="button" onClick={() => onRemove(it)} className="hover:bg-[#c7d2fe] rounded-full p-0.5"><X size={8} /></button>
          </span>
        ))}
        {onAdd && (
          <button
            type="button"
            onClick={() => setPickerOpen(!pickerOpen)}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-[6px] border border-dashed border-[#94a3b8] text-[#475569] hover:bg-[#f1f5f9] text-[10.5px]"
            data-testid={`${testid}-add-toggle`}
          >
            <Plus size={9} /> {pickerOpen ? t("hire.chip_close") : t("hire.chip_add")}
          </button>
        )}
      </div>
      {onAdd && pickerOpen && (
        <div className="mt-1.5 p-2 border border-[#e2e8f0] rounded-[8px] bg-white">
          {remaining.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {remaining.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => { onAdd(opt); }}
                  className="px-2 py-0.5 rounded-[6px] bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#475569] text-[10.5px]"
                  data-testid={`${testid}-add-${opt}`}
                >
                  + {opt}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-1">
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
              className="sw-input !text-[11px] !py-1 flex-1"
              placeholder={t("hire.chip_new_option_ph")}
              data-testid={`${testid}-add-custom`}
            />
            <button type="button" onClick={addCustom} className="sw-btn-secondary !text-[11px] !py-1 !px-2"><Plus size={10} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

const STAGES = [
  { key: "applied", label: "hire.stage_applied", color: "#747474", next: "screening" },
  { key: "screening", label: "hire.stage_screening", color: "#0088ff", next: "interview" },
  { key: "interview", label: "hire.stage_interview", color: "#92400e", next: "approved" },
  { key: "approved", label: "hire.stage_approved", color: "#4338ca", next: "offer_sent" },
  { key: "offer_sent", label: "hire.stage_offer_sent", color: "#475569", next: "offer_signed" },
  { key: "offer_signed", label: "hire.stage_offer_signed", color: "#008932", next: "hired" },
  { key: "hired", label: "hire.stage_hired", color: "#0b430b", next: null },
  { key: "rejected", label: "hire.stage_rejected", color: "#881a27", next: null },
];

const priorityBadge = {
  urgent: "sw-badge-rejected",
  high: "sw-badge-pending",
  medium: "sw-badge-neutral",
  low: "sw-badge-gold",
};

function MoveStageModal({ applicant, defaultStage, onClose, onConfirm }) {
  const { t } = useI18n();
  const [stage, setStage] = useState(defaultStage || applicant.stage);
  const [comment, setComment] = useState("");
  const [interviewerName, setInterviewerName] = useState("");
  const [rating, setRating] = useState("");
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignedInterviewerId, setAssignedInterviewerId] = useState(
    applicant.assigned_interviewer_id || ""
  );

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    if (stage === "interview" && !assignedInterviewerId) return;
    setSaving(true);
    try {
      const assigned = users.find((u) => u.id === assignedInterviewerId);
      await onConfirm({
        stage,
        comment: comment.trim(),
        interviewer_name: interviewerName || null,
        rating: rating ? parseInt(rating) : null,
        assigned_interviewer_id: stage === "interview" ? assignedInterviewerId : null,
        assigned_interviewer_name: stage === "interview" ? (assigned?.name || null) : null,
      });
    } finally { setSaving(false); }
  };

  const showInterviewerPicker = stage === "interview";
  const showRating = stage === "interview" || stage === "screening" || stage === "approved";
  // Don't allow selecting "approved" from this modal — ApproveModal handles that flow
  const allowedStages = STAGES.filter((s) => s.key !== "approved");

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[520px] w-full p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="sw-heading text-[20px]">{t("hire.move_stage_title") || "Move candidate stage"}</h3>
            <p className="text-[13px] text-[#747474] mt-1">{applicant.name}</p>
          </div>
          <button onClick={onClose} className="text-[#747474]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label className="sw-label">{t("hire.new_stage") || "New stage"} *</label>
            <select data-testid="move-stage-select" value={stage} onChange={(e) => setStage(e.target.value)} className="sw-input">
              {allowedStages.map((s) => <option key={s.key} value={s.key}>{t(s.label)}</option>)}
            </select>
          </div>
          {showInterviewerPicker && (
            <div className="bg-[#eef2ff]/40 border border-[#c7d2fe] rounded-[12px] p-3">
              <label className="sw-label !text-[#4338ca]">{t("hire.assigned_interviewer")} *</label>
              <select
                required
                data-testid="assigned-interviewer-select"
                value={assignedInterviewerId}
                onChange={(e) => setAssignedInterviewerId(e.target.value)}
                className="sw-input bg-white"
              >
                <option value="">— {t("hire.pick_interviewer")} —</option>
                {users
                  .filter((u) => u.active !== false)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} · {u.job_title || u.role}
                    </option>
                  ))}
              </select>
              {!assignedInterviewerId && (
                <p className="text-[11px] text-[#c19a2b] mt-1">
                  <AlertTriangle size={10} className="inline mr-1" />
                  {t("hire.pick_interviewer_required")}
                </p>
              )}
            </div>
          )}
          {showRating && !showInterviewerPicker && (
            <>
              <div>
                <label className="sw-label">Interviewer / responsible</label>
                <input value={interviewerName} onChange={(e) => setInterviewerName(e.target.value)} className="sw-input" placeholder="e.g. Carlos Souza" />
              </div>
              <div>
                <label className="sw-label">Rating (1-5)</label>
                <select value={rating} onChange={(e) => setRating(e.target.value)} className="sw-input">
                  <option value="">—</option>
                  <option value="1">1 · Poor</option>
                  <option value="2">2 · Below</option>
                  <option value="3">3 · OK</option>
                  <option value="4">4 · Good</option>
                  <option value="5">5 · Outstanding</option>
                </select>
              </div>
            </>
          )}
          <div>
            <label className="sw-label">Comment * <span className="text-[#881a27] normal-case">(required for audit trail)</span></label>
            <textarea
              data-testid="move-stage-comment"
              required
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="sw-input"
              placeholder={t("hire.observe_ph")}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button
              data-testid="move-stage-confirm"
              type="submit"
              disabled={saving || !comment.trim() || (stage === "interview" && !assignedInterviewerId)}
              className="sw-btn-primary"
            >
              {saving ? "..." : (t("common.confirm") || "Confirm move")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Confirmation modal shown when interviewer moves candidate from Interview → Approved.
// Shows full position snapshot (editable) + full salary matrix picker (all cells).
function ApproveModal({ applicant, vacancy, onClose, onConfirm }) {
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [matrix, setMatrix] = useState(null);
  const [rules, setRules] = useState([]);
  const [masterLists, setMasterLists] = useState({
    behavioral: [], education: [], experience: [], seniority: [], base: [],
    equipment: [], hard_skills: [], soft_skills: [],
  });

  // Seed the confirmed_offer state from vacancy snapshot
  const [form, setForm] = useState(() => {
    const v = vacancy || {};
    const pc = v.payment_components_proposed?.[0] || { format: "monthly", amount: 0 };
    return {
      payroll_company: v.payroll_company || "",
      location: v.location || "",
      job_title: v.title || "",
      department_id: v.department_id || null,
      manager_id: v.hiring_manager_id || null,
      contract_type: v.contract_type || "CLT_FrankCrum",
      access_profile_id: v.access_profile_id || null,
      cost_code: v.cost_code || null,
      salary_matrix_id: v.salary_matrix_id || "",
      salary_cell_id: "",
      salary_amount: pc.amount || 0,
      payment_format: pc.format || "monthly",
      responsibilities: v.responsibilities || "",
      function_description: v.function_description || "",
      behavioral: [...(v.behavioral || [])],
      education: [...(v.education || [])],
      experience: [...(v.experience || [])],
      seniority: [...(v.seniority || [])],
      base: [...(v.base || [])],
      hard_skills: [...(v.hard_skills || [])],
      soft_skills: [...(v.soft_skills || [])],
      equipment: [...(v.equipment || [])],
    };
  });

  useEffect(() => {
    api.get("/pto-rules").then((r) => setRules(r.data || [])).catch(() => setRules([]));
    api.get("/admin/employee-profile-lists")
      .then((r) => setMasterLists(r.data || masterLists))
      .catch(() => {});
    if (form.salary_matrix_id) {
      api.get(`/hiring/salary-matrix-preview?matrix_id=${form.salary_matrix_id}`)
        .then((r) => setMatrix(r.data))
        .catch(() => setMatrix(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locations = Array.from(new Set(rules.map((r) => r.location).filter(Boolean))).sort();
  const payrollCompanies = useMemo(() => {
    const filtered = form.location ? rules.filter((r) => r.location === form.location) : rules;
    return Array.from(new Set(filtered.map((r) => r.payroll_company).filter(Boolean))).sort();
  }, [rules, form.location]);

  const removeChip = (field, val) => setForm((f) => ({ ...f, [field]: (f[field] || []).filter((x) => x !== val) }));
  const addChip = (field, val) => setForm((f) => {
    const arr = f[field] || [];
    if (arr.includes(val)) return f;
    return { ...f, [field]: [...arr, val] };
  });

  const selectedCell = matrix?.cells?.find((c) => c.id === form.salary_cell_id) || null;
  const salaryOutOfBand = selectedCell && form.salary_amount && (
    (selectedCell.salary_min && form.salary_amount < selectedCell.salary_min) ||
    (selectedCell.salary_max && form.salary_amount > selectedCell.salary_max)
  );
  const currencySymbol = matrix?.currency === "BRL" ? "R$" : "$";
  const fmtMoney = (n) => `${currencySymbol}${(n || 0).toLocaleString()}`;

  // Build track × level grid
  const grid = useMemo(() => {
    if (!matrix) return null;
    const cellMap = {};
    (matrix.cells || []).forEach((c) => { cellMap[`${c.track_id}::${c.level_id}`] = c; });
    return { tracks: matrix.tracks || [], levels: matrix.levels || [], cellMap };
  }, [matrix]);

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSaving(true);
    try {
      const confirmed_offer = {
        payroll_company: form.payroll_company || null,
        location: form.location || null,
        job_title: form.job_title,
        department_id: form.department_id || null,
        manager_id: form.manager_id || null,
        contract_type: form.contract_type,
        access_profile_id: form.access_profile_id || null,
        cost_code: form.cost_code || null,
        salary_matrix_id: form.salary_matrix_id || null,
        salary_cell_id: form.salary_cell_id || null,
        salary_amount: parseFloat(form.salary_amount) || 0,
        payment_format: form.payment_format,
        payment_components: [{ format: form.payment_format, amount: parseFloat(form.salary_amount) || 0, label: "Base" }],
        responsibilities: form.responsibilities,
        function_description: form.function_description,
        behavioral: form.behavioral,
        education: form.education,
        experience: form.experience,
        seniority: form.seniority,
        base: form.base,
        hard_skills: form.hard_skills,
        soft_skills: form.soft_skills,
        equipment: form.equipment,
      };
      await onConfirm({ stage: "approved", comment: comment.trim(), confirmed_offer });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[960px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="sw-heading text-[20px]">{t("hire.approve_title")}</h3>
            <p className="text-[13px] text-[#747474] mt-1">
              {applicant.name} · <span className="text-[#4338ca]">{vacancy?.title}</span>
            </p>
            <p className="text-[11.5px] text-[#94a3b8] mt-1">{t("hire.approve_hint")}</p>
          </div>
          <button onClick={onClose} className="text-[#747474]"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4">
          {/* Core position fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="sw-label">{t("hire.job_title")} *</label>
              <input required value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="sw-input" data-testid="approve-job-title" />
            </div>
            <div>
              <label className="sw-label">{t("common.location")} *</label>
              <select required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value, payroll_company: "" })} className="sw-input" data-testid="approve-location">
                <option value="">—</option>
                {locations.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>
            <div>
              <label className="sw-label">{t("admin.payroll_company")} *</label>
              <select required value={form.payroll_company} onChange={(e) => setForm({ ...form, payroll_company: e.target.value })} className="sw-input" data-testid="approve-payroll-company" disabled={!form.location}>
                <option value="">—</option>
                {payrollCompanies.map((pc) => <option key={pc} value={pc}>{pc}</option>)}
                {form.payroll_company && !payrollCompanies.includes(form.payroll_company) && (
                  <option key="__current" value={form.payroll_company}>{form.payroll_company} (atual)</option>
                )}
              </select>
            </div>
          </div>

          {/* Salary Matrix picker */}
          {grid && grid.tracks.length > 0 && (
            <div className="border border-[#e2e8f0] rounded-[14px] p-4 bg-[#f8fafc]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <IdCard size={14} className="text-[#4338ca]" />
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#475569] font-semibold">
                    {t("hire.salary_pick_cell")} · {matrix.name}
                  </div>
                </div>
                {selectedCell && (
                  <div className="text-[11.5px] text-[#4338ca] font-mono">
                    {fmtMoney(selectedCell.salary_min)} – {fmtMoney(selectedCell.salary_max)}
                  </div>
                )}
              </div>
              <div className="overflow-x-auto" data-testid="approve-matrix-grid">
                <table className="text-[11px] w-full">
                  <thead>
                    <tr>
                      <th className="text-left px-2 py-1 text-[#747474]"></th>
                      {grid.levels.map((lv) => (
                        <th key={lv.id} className="px-2 py-1 text-[#475569] font-medium">{lv.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grid.tracks.map((tk) => (
                      <tr key={tk.id}>
                        <td className="text-left px-2 py-1 font-medium text-[#475569]">{tk.name}</td>
                        {grid.levels.map((lv) => {
                          const cell = grid.cellMap[`${tk.id}::${lv.id}`];
                          if (!cell) return <td key={lv.id} className="px-1 py-1"><div className="text-[10px] text-[#e2e8f0]">—</div></td>;
                          const isSelected = form.salary_cell_id === cell.id;
                          return (
                            <td key={lv.id} className="px-1 py-1">
                              <button
                                type="button"
                                data-testid={`matrix-cell-${cell.id}`}
                                onClick={() => setForm((f) => ({ ...f, salary_cell_id: cell.id, salary_amount: cell.salary_min || f.salary_amount }))}
                                className={`w-full px-2 py-1.5 rounded-[8px] text-left border transition-all ${
                                  isSelected
                                    ? "bg-[#4338ca] text-white border-[#4338ca]"
                                    : "bg-white text-[#0f172a] border-[#e2e8f0] hover:border-[#4338ca]"
                                }`}
                              >
                                <div className="font-mono text-[10.5px]">
                                  {fmtMoney(cell.salary_min)}
                                </div>
                                <div className={`font-mono text-[10.5px] ${isSelected ? "opacity-80" : "text-[#94a3b8]"}`}>
                                  – {fmtMoney(cell.salary_max)}
                                </div>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="sw-label">{t("hire.payment_format")}</label>
                  <select value={form.payment_format} onChange={(e) => setForm({ ...form, payment_format: e.target.value })} className="sw-input" data-testid="approve-payment-format">
                    <option value="hourly">{t("admin.payment.per_hour")}</option>
                    <option value="daily">{t("admin.payment.per_day")}</option>
                    <option value="monthly">{t("admin.payment.per_month")}</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="sw-label">{t("hire.negotiated_salary")} *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.salary_amount}
                    onChange={(e) => setForm({ ...form, salary_amount: e.target.value })}
                    className={`sw-input ${salaryOutOfBand ? "!border-[#c19a2b] bg-[#fef8e7]" : ""}`}
                    data-testid="approve-salary-amount"
                    required
                  />
                  {selectedCell && salaryOutOfBand && (
                    <p className="text-[11px] text-[#c19a2b] mt-1">
                      <AlertTriangle size={10} className="inline mr-1" />
                      {t("hire.salary_outside_band", { min: selectedCell.salary_min || 0, max: selectedCell.salary_max || 0 })}
                    </p>
                  )}
                  {selectedCell && !salaryOutOfBand && form.salary_amount > 0 && (
                    <p className="text-[11px] text-[#008932] mt-1">
                      <CheckCircle2 size={10} className="inline mr-1" />
                      {t("hire.salary_within_band")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Snapshot chips (editable) */}
          <div className="border border-[#e2e8f0] rounded-[14px] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-[#4338ca]" />
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#475569] font-semibold">{t("hire.position_snapshot")}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11.5px]">
              <ChipsRow testid="app-behavioral" label={t("admin.dimension_behavioral")} items={form.behavioral} options={masterLists.behavioral} onRemove={(v) => removeChip("behavioral", v)} onAdd={(v) => addChip("behavioral", v)} />
              <ChipsRow testid="app-education" label={t("admin.dimension_education")} items={form.education} options={masterLists.education} onRemove={(v) => removeChip("education", v)} onAdd={(v) => addChip("education", v)} />
              <ChipsRow testid="app-experience" label={t("admin.dimension_experience")} items={form.experience} options={masterLists.experience} onRemove={(v) => removeChip("experience", v)} onAdd={(v) => addChip("experience", v)} />
              <ChipsRow testid="app-seniority" label={t("admin.dimension_seniority")} items={form.seniority} options={masterLists.seniority} onRemove={(v) => removeChip("seniority", v)} onAdd={(v) => addChip("seniority", v)} />
              <ChipsRow testid="app-base" label={t("admin.dimension_base")} items={form.base} options={masterLists.base} onRemove={(v) => removeChip("base", v)} onAdd={(v) => addChip("base", v)} />
              <ChipsRow testid="app-hard-skills" label={t("admin.hard_skills")} items={form.hard_skills} options={masterLists.hard_skills} onRemove={(v) => removeChip("hard_skills", v)} onAdd={(v) => addChip("hard_skills", v)} />
              <div className="md:col-span-2">
                <ChipsRow testid="app-soft-skills" label={t("admin.soft_skills")} items={form.soft_skills} options={masterLists.soft_skills} onRemove={(v) => removeChip("soft_skills", v)} onAdd={(v) => addChip("soft_skills", v)} />
              </div>
              <div className="md:col-span-2">
                <ChipsRow
                  testid="app-equipment"
                  label={t("hire.equipment_needed")}
                  items={form.equipment}
                  options={(masterLists.equipment || []).map((x) => typeof x === "string" ? x : x.name).filter(Boolean)}
                  onRemove={(v) => removeChip("equipment", v)}
                  onAdd={(v) => addChip("equipment", v)}
                />
              </div>
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="sw-label">Comment *</label>
            <textarea
              required
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="sw-input"
              placeholder={t("hire.observe_ph")}
              data-testid="approve-comment"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button
              type="submit"
              disabled={saving || !comment.trim() || !form.job_title || !form.location || !form.payroll_company}
              className="sw-btn-primary"
              data-testid="confirm-approve-btn"
            >
              {saving ? "..." : t("hire.confirm_approval")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



function EquipmentEditor({ value, onChange }) {
  const { t } = useI18n();
  const [items, setItems] = useState(value || []);
  const [newItem, setNewItem] = useState("");
  useEffect(() => { onChange(items); }, [items]); // eslint-disable-line
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[24px]">
        {items.map((it, i) => (
          <span key={i} className="sw-badge sw-badge-gold inline-flex items-center gap-1">
            {it}
            <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="hover:bg-[#94a3b8] rounded-full p-0.5"><X size={10} /></button>
          </span>
        ))}
        {items.length === 0 && <span className="text-[12px] text-[#747474]">{t("hire.no_equipment")}</span>}
      </div>
      <div className="flex gap-2">
        <input value={newItem} onChange={(e) => setNewItem(e.target.value)} className="sw-input flex-1" placeholder="e.g. Hard hat, Laptop, Steel-toe boots" />
        <button type="button" onClick={() => { if (newItem.trim()) { setItems([...items, newItem.trim()]); setNewItem(""); } }} className="sw-btn-secondary"><Plus size={14} /></button>
      </div>
    </div>
  );
}

function NewVacancyModal({ onClose, onSaved, departments, jobProfiles, accessProfiles, vacancyToEdit }) {
  const { t } = useI18n();
  const isEdit = !!vacancyToEdit;
  const [form, setForm] = useState(() => {
    const v = vacancyToEdit || {};
    const paymentComp = v.payment_components_proposed?.[0];
    return {
      title: v.title || "", department_id: v.department_id || "", location: v.location || "",
      payroll_company: v.payroll_company || "",
      job_description: v.job_description || "", priority: v.priority || "medium", headcount: v.headcount || 1,
      target_start_date: v.target_start_date || "", hiring_manager_id: v.hiring_manager_id || "",
      job_profile_id: v.job_profile_id || "", access_profile_id: v.access_profile_id || "",
      equipment: v.equipment || [],
      payment_format: paymentComp?.format || "monthly",
      payment_amount: paymentComp?.amount || 5000,
      behavioral: v.behavioral || [], education: v.education || [], experience: v.experience || [],
      seniority: v.seniority || [], base: v.base || [],
      responsibilities: v.responsibilities || "", function_description: v.function_description || "",
      salary_matrix_id: v.salary_matrix_id || "", salary_quadrants: v.salary_quadrants || [],
      hard_skills: v.hard_skills || [], soft_skills: v.soft_skills || [],
    };
  });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [rules, setRules] = useState([]);
  const [matrixData, setMatrixData] = useState(null); // { tracks, levels, cells } of the position's matrix
  const [masterLists, setMasterLists] = useState({
    behavioral: [], education: [], experience: [], seniority: [], base: [],
    equipment: [], hard_skills: [], soft_skills: [],
  });

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/pto-rules").then((r) => setRules(r.data || [])).catch(() => setRules([]));
    api.get("/admin/employee-profile-lists")
      .then((r) => setMasterLists(r.data || masterLists))
      .catch(() => { /* keep defaults */ });
    // If editing and vacancy already has a matrix, load it
    if (isEdit && vacancyToEdit?.salary_matrix_id) {
      loadMatrix(vacancyToEdit.salary_matrix_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Equipment master (array of {name, department_id}) → names for options
  const equipmentOptions = useMemo(() => (masterLists.equipment || []).map((x) => typeof x === "string" ? x : x.name).filter(Boolean), [masterLists.equipment]);

  // Distinct locations & payroll companies from Rules
  const locations = Array.from(new Set(rules.map((r) => r.location).filter(Boolean))).sort();
  const payrollCompanies = useMemo(() => {
    const filtered = form.location ? rules.filter((r) => r.location === form.location) : rules;
    return Array.from(new Set(filtered.map((r) => r.payroll_company).filter(Boolean))).sort();
  }, [rules, form.location]);

  // Load matrix when position changes
  const loadMatrix = async (matrixId) => {
    if (!matrixId) { setMatrixData(null); return; }
    try {
      const { data } = await api.get(`/salary-matrix?matrix_id=${matrixId}`);
      setMatrixData({ tracks: data.tracks || [], levels: data.levels || [], cells: data.cells || [], currency: data.currency || "USD" });
    } catch { setMatrixData(null); }
  };

  const applyProfile = (profileId) => {
    if (!profileId) {
      setForm((f) => ({ ...f, job_profile_id: "" }));
      setMatrixData(null);
      return;
    }
    const p = jobProfiles.find((x) => x.id === profileId);
    if (!p) return;
    const firstComp = p.payment_components_default?.[0];
    setForm((f) => ({
      ...f,
      job_profile_id: p.id,
      title: p.job_title || p.name,
      department_id: p.department_id || f.department_id,
      // If position has one payroll company, pre-select; else keep current
      payroll_company: (p.payroll_companies && p.payroll_companies.length === 1) ? p.payroll_companies[0] : f.payroll_company,
      access_profile_id: p.access_profile_id || "",
      equipment: [...(p.equipment || [])],
      payment_format: firstComp?.format || f.payment_format,
      payment_amount: firstComp?.amount || f.payment_amount,
      job_description: p.description || f.job_description,
      // Snapshot from Position
      behavioral: [...(p.behavioral || [])],
      education: [...(p.education || [])],
      experience: [...(p.experience || [])],
      seniority: [...(p.seniority || [])],
      base: [...(p.base || [])],
      responsibilities: p.responsibilities || "",
      function_description: p.function_description || "",
      salary_matrix_id: p.salary_matrix_id || "",
      salary_quadrants: [...(p.salary_quadrants || [])],
      hard_skills: [...(p.hard_skills || [])],
      soft_skills: [...(p.soft_skills || [])],
    }));
    loadMatrix(p.salary_matrix_id);
  };

  // Compute allowed salary range from selected quadrants of the matrix
  const allowedRange = useMemo(() => {
    if (!matrixData || !form.salary_quadrants?.length) return null;
    const selectedCells = matrixData.cells.filter((c) => form.salary_quadrants.includes(c.id));
    if (!selectedCells.length) return null;
    const mins = selectedCells.map((c) => c.salary_min || 0).filter((x) => x > 0);
    const maxs = selectedCells.map((c) => c.salary_max || 0).filter((x) => x > 0);
    return {
      min: mins.length ? Math.min(...mins) : null,
      max: maxs.length ? Math.max(...maxs) : null,
      currency: matrixData.currency,
    };
  }, [matrixData, form.salary_quadrants]);

  const amountInRange = () => {
    if (!allowedRange) return true;
    const a = parseFloat(form.payment_amount);
    if (!a || form.payment_format !== "monthly") return true; // only monthly is compared
    if (allowedRange.min && a < allowedRange.min) return false;
    if (allowedRange.max && a > allowedRange.max) return false;
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        department_id: form.department_id || null,
        location: form.location,
        payroll_company: form.payroll_company || null,
        hiring_manager_id: form.hiring_manager_id || null,
        job_description: form.job_description || null,
        priority: form.priority,
        headcount: parseInt(form.headcount) || 1,
        target_start_date: form.target_start_date || null,
        job_profile_id: form.job_profile_id || null,
        access_profile_id: form.access_profile_id || null,
        equipment: form.equipment,
        payment_components_proposed: [{ format: form.payment_format, amount: parseFloat(form.payment_amount), label: "Base" }],
        // Position snapshot
        behavioral: form.behavioral, education: form.education, experience: form.experience,
        seniority: form.seniority, base: form.base,
        responsibilities: form.responsibilities || null,
        function_description: form.function_description || null,
        salary_matrix_id: form.salary_matrix_id || null,
        salary_quadrants: form.salary_quadrants,
        hard_skills: form.hard_skills, soft_skills: form.soft_skills,
      };
      if (isEdit) {
        await api.put(`/vacancies/${vacancyToEdit.id}`, payload);
      } else {
        await api.post("/vacancies", payload);
      }
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const removeChip = (field, val) => setForm((f) => ({ ...f, [field]: (f[field] || []).filter((x) => x !== val) }));
  const addChip = (field, val) => setForm((f) => {
    const arr = f[field] || [];
    if (arr.includes(val)) return f;
    return { ...f, [field]: [...arr, val] };
  });

  // Allowed quadrants → list per row (track · level: band)
  const allowedQuadrants = useMemo(() => {
    if (!matrixData || !form.salary_quadrants?.length) return [];
    const trackById = Object.fromEntries((matrixData.tracks || []).map((t) => [t.id, t.name]));
    const levelById = Object.fromEntries((matrixData.levels || []).map((l) => [l.id, l.name]));
    return matrixData.cells
      .filter((c) => form.salary_quadrants.includes(c.id))
      .map((c) => ({
        id: c.id,
        label: `${trackById[c.track_id] || "?"} · ${levelById[c.level_id] || "?"}`,
        min: c.salary_min || 0,
        max: c.salary_max || 0,
        currency: matrixData.currency,
      }));
  }, [matrixData, form.salary_quadrants]);
  const currencySymbol = (allowedQuadrants[0]?.currency === "BRL") ? "R$" : "$";
  const fmtMoney = (n) => `${currencySymbol}${(n || 0).toLocaleString()}`;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[820px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 className="sw-heading text-[20px]">{t("hire.new_vacancy")}</h3>
          <button onClick={onClose} className="text-[#747474]"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Position picker */}
          <div className="md:col-span-2 bg-[#e2e8f0]/40 border border-[#94a3b8] rounded-[12px] p-4">
            <label className="sw-label !text-[#475569]">{t("hire.position_select")}</label>
            <select
              data-testid="job-profile-select"
              value={form.job_profile_id}
              onChange={(e) => applyProfile(e.target.value)}
              className="sw-input bg-white"
            >
              <option value="">— {t("hire.custom_fill")} —</option>
              {jobProfiles.filter((p) => p.active).map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.payroll_companies?.length ? ` · ${p.payroll_companies.join("/")}` : ""}</option>
              ))}
            </select>
            <div className="text-[11px] text-[#747474] mt-2">{t("hire.picking_hint")}</div>
          </div>

          {/* Core fields */}
          <div className="md:col-span-2">
            <label className="sw-label">{t("hire.job_title")} *</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="sw-input" data-testid="vacancy-title" />
          </div>
          <div>
            <label className="sw-label">{t("common.department")} *</label>
            <select required value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="sw-input" data-testid="vacancy-department">
              <option value="">—</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="sw-label">{t("hire.reports_to")}</label>
            <select value={form.hiring_manager_id} onChange={(e) => setForm({ ...form, hiring_manager_id: e.target.value })} className="sw-input">
              <option value="">—</option>
              {users.filter((u) => ["manager", "hr", "admin", "ceo"].includes(u.role)).map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.job_title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="sw-label">{t("common.location")} *</label>
            <select required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value, payroll_company: "" })} className="sw-input" data-testid="vacancy-location">
              <option value="">—</option>
              {locations.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
            </select>
            {locations.length === 0 && <p className="text-[11px] text-[#94a3b8] mt-1">{t("hire.no_locations")}</p>}
          </div>
          <div>
            <label className="sw-label">{t("admin.payroll_company")} *</label>
            <select required value={form.payroll_company} onChange={(e) => setForm({ ...form, payroll_company: e.target.value })} className="sw-input" data-testid="vacancy-payroll-company" disabled={!form.location}>
              <option value="">—</option>
              {payrollCompanies.map((pc) => <option key={pc} value={pc}>{pc}</option>)}
              {form.payroll_company && !payrollCompanies.includes(form.payroll_company) && (
                <option key="__current" value={form.payroll_company}>{form.payroll_company} (atual)</option>
              )}
            </select>
            {!form.location && <p className="text-[11px] text-[#94a3b8] mt-1">{t("hire.select_location_first")}</p>}
          </div>
          <div>
            <label className="sw-label">Headcount</label>
            <input type="number" min="1" value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("hire.access_profile")} *</label>
            <select required value={form.access_profile_id} onChange={(e) => setForm({ ...form, access_profile_id: e.target.value })} className="sw-input">
              <option value="">— {t("hire.select")} —</option>
              {accessProfiles.filter((p) => p.active).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
              {form.access_profile_id && !accessProfiles.some((p) => p.id === form.access_profile_id && p.active) && (
                <option key="__current" value={form.access_profile_id}>
                  {accessProfiles.find((p) => p.id === form.access_profile_id)?.name || form.access_profile_id} (atual)
                </option>
              )}
            </select>
          </div>
          <div>
            <label className="sw-label">Priority</label>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="sw-input">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
            </select>
          </div>
          <div>
            <label className="sw-label">{t("hire.target_start")}</label>
            <input type="date" value={form.target_start_date} onChange={(e) => setForm({ ...form, target_start_date: e.target.value })} className="sw-input" />
          </div>

          {/* === Position preview section (editable) === */}
          {form.job_profile_id && (
            <div className="md:col-span-2 bg-[#f8fafc] border border-[#e2e8f0] rounded-[14px] p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-[#4338ca]" />
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#475569] font-semibold">{t("hire.position_snapshot")}</div>
              </div>
              <p className="text-[11.5px] text-[#94a3b8] mb-3">{t("hire.position_snapshot_hint")}</p>
              <div className="grid grid-cols-2 gap-3 text-[11.5px]">
                <ChipsRow testid="vac-behavioral" label={t("admin.dimension_behavioral")} items={form.behavioral} options={masterLists.behavioral} onRemove={(v) => removeChip("behavioral", v)} onAdd={(v) => addChip("behavioral", v)} />
                <ChipsRow testid="vac-education" label={t("admin.dimension_education")} items={form.education} options={masterLists.education} onRemove={(v) => removeChip("education", v)} onAdd={(v) => addChip("education", v)} />
                <ChipsRow testid="vac-experience" label={t("admin.dimension_experience")} items={form.experience} options={masterLists.experience} onRemove={(v) => removeChip("experience", v)} onAdd={(v) => addChip("experience", v)} />
                <ChipsRow testid="vac-seniority" label={t("admin.dimension_seniority")} items={form.seniority} options={masterLists.seniority} onRemove={(v) => removeChip("seniority", v)} onAdd={(v) => addChip("seniority", v)} />
                <ChipsRow testid="vac-base" label={t("admin.dimension_base")} items={form.base} options={masterLists.base} onRemove={(v) => removeChip("base", v)} onAdd={(v) => addChip("base", v)} />
                <ChipsRow testid="vac-hard-skills" label={t("admin.hard_skills")} items={form.hard_skills} options={masterLists.hard_skills} onRemove={(v) => removeChip("hard_skills", v)} onAdd={(v) => addChip("hard_skills", v)} />
                <div className="col-span-2">
                  <ChipsRow testid="vac-soft-skills" label={t("admin.soft_skills")} items={form.soft_skills} options={masterLists.soft_skills} onRemove={(v) => removeChip("soft_skills", v)} onAdd={(v) => addChip("soft_skills", v)} />
                </div>
                <div className="col-span-2">
                  <ChipsRow testid="vac-equipment" label={t("hire.equipment_needed")} items={form.equipment} options={equipmentOptions} onRemove={(v) => removeChip("equipment", v)} onAdd={(v) => addChip("equipment", v)} />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3">
                {form.responsibilities && (
                  <div>
                    <label className="sw-label !text-[10.5px]">{t("admin.responsibilities")}</label>
                    <textarea rows={2} value={form.responsibilities} onChange={(e) => setForm({ ...form, responsibilities: e.target.value })} className="sw-input !text-[12px]" data-testid="vacancy-responsibilities" />
                  </div>
                )}
                {form.function_description && (
                  <div>
                    <label className="sw-label !text-[10.5px]">{t("admin.function_desc")}</label>
                    <textarea rows={2} value={form.function_description} onChange={(e) => setForm({ ...form, function_description: e.target.value })} className="sw-input !text-[12px]" data-testid="vacancy-function" />
                  </div>
                )}
              </div>

              {/* Salary quadrants list */}
              {allowedQuadrants.length > 0 && (
                <div className="mt-3 bg-white border border-[#e0e7ff] rounded-[10px] p-3" data-testid="allowed-quadrants-list">
                  <div className="flex items-center gap-2 mb-2">
                    <IdCard size={13} className="text-[#4338ca]" />
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#475569] font-semibold">{t("hire.allowed_band")}</div>
                    <span className="text-[10px] text-[#94a3b8]">({allowedQuadrants.length} {t("hire.quadrants_short")})</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {allowedQuadrants.map((q) => (
                      <div key={q.id} className="flex items-center justify-between px-2.5 py-1.5 bg-[#f8fafc] rounded-[8px] text-[11.5px]">
                        <span className="text-[#0f172a] font-medium">{q.label}</span>
                        <span className="text-[#4338ca] font-mono">{fmtMoney(q.min)} – {fmtMoney(q.max)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Payment */}
          <div>
            <label className="sw-label">{t("hire.payment_format")}</label>
            <select value={form.payment_format} onChange={(e) => setForm({ ...form, payment_format: e.target.value })} className="sw-input" data-testid="vacancy-payment-format">
              <option value="hourly">{t("admin.payment.per_hour")}</option>
              <option value="daily">{t("admin.payment.per_day")}</option>
              <option value="monthly">{t("admin.payment.per_month")}</option>
            </select>
          </div>
          <div>
            <label className="sw-label">{t("hire.payment_amount")}</label>
            <input type="number" step="0.01" value={form.payment_amount} onChange={(e) => setForm({ ...form, payment_amount: e.target.value })} className={`sw-input ${!amountInRange() ? "!border-[#c19a2b] bg-[#fef8e7]" : ""}`} data-testid="vacancy-payment-amount" />
            {allowedRange && form.payment_format === "monthly" && !amountInRange() && (
              <p className="text-[11px] text-[#c19a2b] mt-1">
                <AlertTriangle size={10} className="inline mr-1" />
                {t("hire.out_of_range", { min: allowedRange.min || 0, max: allowedRange.max || 0 })}
              </p>
            )}
          </div>

          {/* Equipment (visible in custom mode too) */}
          {!form.job_profile_id && (
            <div className="md:col-span-2">
              <label className="sw-label">{t("hire.equipment_needed")}</label>
              <ChipsRow testid="vac-equipment-custom" label="" items={form.equipment} options={equipmentOptions} onRemove={(v) => removeChip("equipment", v)} onAdd={(v) => addChip("equipment", v)} />
            </div>
          )}

          <div className="md:col-span-2">
            <label className="sw-label">{t("hire.job_desc")}</label>
            <textarea rows={4} value={form.job_description} onChange={(e) => setForm({ ...form, job_description: e.target.value })} className="sw-input" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="save-vacancy-btn" type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : (isEdit ? t("common.save") : t("hire.create_draft"))}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewApplicantModal({ vacancy, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", birth_date: "", personal_email: "", source: "LinkedIn", notes: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/applicants", { vacancy_id: vacancy.id, ...form });
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[560px] w-full p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="sw-heading text-[20px]">{t("hire.new_applicant")}</h3>
            <p className="text-[13px] text-[#747474] mt-1">{vacancy.title}</p>
          </div>
          <button onClick={onClose} className="text-[#747474]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="sw-label">Full name *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">Email *</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">Phone</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">Birthday</label>
            <input data-testid="applicant-birth-date" type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("common.personal_email")}</label>
            <input type="email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} className="sw-input" />
          </div>
          <div className="md:col-span-2 text-[11px] text-[#94a3b8]">💡 Birthday and personal email entered here will auto-populate the Directory and Org Chart when the candidate is hired.</div>
          <div>
            <label className="sw-label">Source</label>
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="sw-input">
              <option>LinkedIn</option><option>Indeed</option><option>Referral</option><option>Direct</option><option>Other</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="sw-label">Initial notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sw-input" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="save-applicant-btn" type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : "Add"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ApplicantDetailModal({ applicant, vacancy, onClose, onUpdated }) {
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const [working, setWorking] = useState(false);
  const [hireForm, setHireForm] = useState({ password: "Sagewood@2026", role: "employee" });
  const [showHire, setShowHire] = useState(false);
  const [moveStageOpen, setMoveStageOpen] = useState(false);
  const [moveStageDefault, setMoveStageDefault] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);

  const action = async (fn) => {
    setWorking(true);
    try { await fn(); await onUpdated(); }
    catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setWorking(false); }
  };

  const openMoveStage = (defaultStage) => { setMoveStageDefault(defaultStage); setMoveStageOpen(true); };
  const confirmMoveStage = async (payload) => {
    await api.post(`/applicants/${applicant.id}/stage`, payload);
    setMoveStageOpen(false);
    await onUpdated();
  };
  const confirmApproved = async (payload) => {
    await api.post(`/applicants/${applicant.id}/stage`, payload);
    setApproveOpen(false);
    await onUpdated();
  };
  const runBackground = () => action(() => api.post(`/applicants/${applicant.id}/background-check`));
  const sendOffer = () => action(() => api.post(`/applicants/${applicant.id}/send-offer`, {
    payment_components: applicant.confirmed_offer?.payment_components || vacancy?.payment_components_proposed || [],
    start_date: vacancy?.target_start_date,
    custom_message: "Welcome to Sagewood!",
  }));
  const signOffer = () => action(() => api.post(`/applicants/${applicant.id}/sign-offer`));
  const hire = async () => {
    setWorking(true);
    try {
      await api.post(`/applicants/${applicant.id}/hire`, hireForm);
      await onUpdated();
      onClose();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setWorking(false); }
  };

  // Role & interviewer awareness
  const isHR = currentUser && ["hr", "admin", "ceo"].includes(currentUser.role);
  const isManager = currentUser?.role === "manager";
  const isAssignedInterviewer = applicant.assigned_interviewer_id === currentUser?.id;
  // At interview stage, only the assigned interviewer (or manager/HR) can move
  const canMoveFromInterview = isHR || isManager || isAssignedInterviewer;
  const atInterview = applicant.stage === "interview";
  const canAct = !atInterview || canMoveFromInterview;

  const currentStageDef = STAGES.find((s) => s.key === applicant.stage);
  const nextStage = currentStageDef?.next;
  const confirmed = applicant.confirmed_offer || null;
  const currencySymbol = "$";
  const fmtMoney = (n) => `${currencySymbol}${(n || 0).toLocaleString()}`;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[720px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{vacancy?.title}</div>
            <h3 className="sw-heading text-[22px] mt-1">{applicant.name}</h3>
            <div className="flex flex-wrap gap-3 text-[13px] text-[#747474] mt-1">
              <span className="flex items-center gap-1"><Mail size={12} /> {applicant.email}</span>
              {applicant.phone && <span className="flex items-center gap-1"><Phone size={12} /> {applicant.phone}</span>}
              {applicant.source && <span className="sw-badge sw-badge-neutral !py-0">{applicant.source}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-[#747474]"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className={`rounded-[12px] p-3 ${applicant.background_check_status ? "bg-[#b8e6cd]" : "bg-[#f6f6f6]"}`}>
            <div className="flex items-center gap-2 text-[12px] font-medium text-[#323232]"><ShieldCheck size={14} /> Background check</div>
            <div className="text-[12px] text-[#747474] mt-1">
              {applicant.background_check_status
                ? `${applicant.background_check_status.status.toUpperCase()} · ${applicant.background_check_status.reference}`
                : "Not requested"}
            </div>
          </div>
          <div className={`rounded-[12px] p-3 ${applicant.offer ? "bg-[#e2e8f0]" : "bg-[#f6f6f6]"}`}>
            <div className="flex items-center gap-2 text-[12px] font-medium text-[#323232]"><FileSignature size={14} /> DocuSign offer</div>
            <div className="text-[12px] text-[#747474] mt-1">
              {applicant.offer ? `${applicant.offer.status.toUpperCase()} · ${applicant.offer.envelope_id}` : "Not sent"}
            </div>
          </div>
        </div>

        <div className="bg-[#f6f6f6] rounded-[14px] p-4 mb-5">
          <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3">Actions · current: <b>{applicant.stage}</b></div>

          {/* Interviewer banner */}
          {atInterview && applicant.assigned_interviewer_name && (
            <div className="bg-white border border-[#c7d2fe] rounded-[10px] p-3 mb-3" data-testid="interviewer-banner">
              <div className="text-[11px] uppercase tracking-wider text-[#4338ca] font-semibold mb-1">
                {t("hire.assigned_interviewer")}
              </div>
              <div className="text-[13px] text-[#0f172a]">{applicant.assigned_interviewer_name}</div>
              {!canMoveFromInterview && (
                <p className="text-[11px] text-[#c19a2b] mt-1">
                  <AlertTriangle size={10} className="inline mr-1" />
                  {t("hire.only_assigned_can_move")}
                </p>
              )}
            </div>
          )}

          {/* Approved summary banner */}
          {applicant.stage === "approved" && confirmed && (
            <div className="bg-white border border-[#e0e7ff] rounded-[10px] p-3 mb-3" data-testid="approved-summary">
              <div className="text-[11px] uppercase tracking-wider text-[#4338ca] font-semibold mb-2">
                {t("hire.approved_summary")}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px] text-[#323232]">
                <div><span className="text-[#747474]">{t("hire.job_title")}:</span> <b>{confirmed.job_title}</b></div>
                <div><span className="text-[#747474]">{t("common.location")}:</span> {confirmed.location}</div>
                <div><span className="text-[#747474]">{t("admin.payroll_company")}:</span> {confirmed.payroll_company}</div>
                <div><span className="text-[#747474]">{t("hire.negotiated_salary")}:</span> <b className="text-[#4338ca] font-mono">{fmtMoney(confirmed.salary_amount)} / {confirmed.payment_format}</b></div>
              </div>
              {confirmed.equipment?.length > 0 && (
                <div className="mt-2 text-[11.5px] text-[#475569]">
                  <Wrench size={11} className="inline mr-1" /> {confirmed.equipment.length} item(s)
                </div>
              )}
              {isHR && (
                <p className="text-[11px] text-[#94a3b8] mt-2">{t("hire.reassign_interviewer_hint")}</p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {/* Simple next-stage button (for stages that don't require special modals) */}
            {nextStage && nextStage !== "hired" && nextStage !== "approved" && canAct && (
              <button data-testid="next-stage-btn" onClick={() => openMoveStage(nextStage)} disabled={working} className="sw-btn-secondary">
                {t("hire.move_to")} {t(STAGES.find((s) => s.key === nextStage).label)} <ArrowRight size={12} />
              </button>
            )}

            {/* Interview stage: reassign interviewer or send to Approved */}
            {atInterview && canMoveFromInterview && (
              <>
                <button
                  data-testid="reassign-interviewer-btn"
                  onClick={() => openMoveStage("interview")}
                  disabled={working}
                  className="sw-btn-secondary"
                >
                  <UserPlus size={12} /> {t("hire.forward_to_interviewer")}
                </button>
                <button
                  data-testid="move-to-approved-btn"
                  onClick={() => setApproveOpen(true)}
                  disabled={working}
                  className="sw-btn-primary !bg-[#4338ca] hover:!bg-[#312e81]"
                >
                  <CheckCircle2 size={12} /> {t("hire.move_to_approved")}
                </button>
              </>
            )}

            {/* HR flow: Approved → Offer Sent → Offer Signed → Hired */}
            {applicant.stage === "approved" && isHR && !applicant.offer && (
              <button data-testid="send-offer-letter-btn" onClick={sendOffer} disabled={working} className="sw-btn-primary">
                <FileSignature size={12} /> {t("hire.send_offer_letter")}
              </button>
            )}
            {applicant.offer && applicant.offer.status === "sent" && isHR && (
              <button data-testid="sign-offer-btn" onClick={signOffer} disabled={working} className="sw-btn-secondary">
                <CheckCircle2 size={12} /> {t("hire.mark_offer_signed")}
              </button>
            )}
            {applicant.stage === "offer_signed" && !applicant.hired_user_id && isHR && (
              <button data-testid="hire-btn" onClick={() => setShowHire(true)} disabled={working} className="sw-btn-primary !bg-[#008932] hover:!bg-[#0b430b]">
                <UserPlus size={12} /> {t("hire.hire_create")}
              </button>
            )}

            {/* Generic move-stage modal (change stage with comment) */}
            {canAct && (
              <button data-testid="move-stage-btn" onClick={() => openMoveStage(applicant.stage)} disabled={working} className="sw-btn-secondary">
                {t("hire.change_stage") || "Change stage with comment"}
              </button>
            )}
            {applicant.stage === "interview" && !applicant.background_check_status && canMoveFromInterview && (
              <button data-testid="bg-check-btn" onClick={runBackground} disabled={working} className="sw-btn-secondary"><ShieldCheck size={12} /> Run background check</button>
            )}
            {applicant.stage !== "hired" && applicant.stage !== "rejected" && canAct && (
              <button onClick={() => openMoveStage("rejected")} disabled={working} className="sw-btn-secondary !text-[#881a27] !border-[#e5c1c1]"><XCircle size={12} /> {t("common.reject")}</button>
            )}
            {applicant.hired_user_id && (
              <span className="sw-badge sw-badge-approved"><CheckCircle2 size={12} /> Hired · User #{applicant.hired_user_id.slice(0, 8)}</span>
            )}
          </div>

          {showHire && (
            <div className="mt-4 bg-white rounded-[10px] p-3 border border-[#475569]">
              <div className="text-[12px] text-[#747474] mb-2">
                {confirmed
                  ? `Confirmed: ${confirmed.job_title} · ${confirmed.location} · ${confirmed.payroll_company} · ${fmtMoney(confirmed.salary_amount)}/${confirmed.payment_format}. Onboarding tasks + equipment will be auto-applied.`
                  : "Set initial password for portal access. Onboarding tasks + equipment from vacancy will be auto-applied."}
              </div>
              <div className="flex gap-2">
                <input data-testid="hire-password-input" type="text" value={hireForm.password} onChange={(e) => setHireForm({ ...hireForm, password: e.target.value })} className="sw-input" placeholder={t("hire.initial_pwd_ph")} />
                <button data-testid="confirm-hire-btn" onClick={hire} disabled={working} className="sw-btn-primary">{working ? "..." : "Confirm hire"}</button>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-2">Timeline</div>
          <div className="flex flex-col gap-3">
            {(applicant.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="flex items-start gap-3 text-[13px] bg-white rounded-[10px] p-3 border border-[#eeeded]">
                <div className="w-2 h-2 rounded-full bg-[#475569] mt-2 shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium capitalize">{h.stage.replace("_", " ")}</span>
                    {h.rating && <span className="sw-badge sw-badge-gold">★ {h.rating}/5</span>}
                    {h.interviewer_name && <span className="sw-badge sw-badge-neutral">Interviewer: {h.interviewer_name}</span>}
                  </div>
                  {h.comment && <div className="text-[12px] text-[#323232] mt-1 italic">&ldquo;{h.comment}&rdquo;</div>}
                  <div className="text-[11px] text-[#747474] mt-1">{h.by_name || "—"} · {h.at?.split("T")[0]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {moveStageOpen && (
          <MoveStageModal
            applicant={applicant}
            defaultStage={moveStageDefault}
            onClose={() => setMoveStageOpen(false)}
            onConfirm={confirmMoveStage}
          />
        )}
        {approveOpen && (
          <ApproveModal
            applicant={applicant}
            vacancy={vacancy}
            onClose={() => setApproveOpen(false)}
            onConfirm={confirmApproved}
          />
        )}
      </div>
    </div>
  );
}

// Compact KPI card used in the recruitment scoreboard
function KpiCard({ label, value, sub, testid, color = "#475569" }) {
  return (
    <div className="sw-card !p-4" data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <div className="sw-heading text-[28px]" style={{ color }}>{value}</div>
      </div>
      {sub && <div className="text-[11px] text-[#747474] mt-1">{sub}</div>}
    </div>
  );
}

function RecruitmentScoreboard({ vacancies, applicants, departments }) {
  const { t } = useI18n();
  const [board, setBoard] = useState(null);
  useEffect(() => {
    api.get("/hiring/scoreboard").then((r) => setBoard(r.data)).catch(() => setBoard(null));
  }, [applicants.length, vacancies.length]);

  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";
  if (!board) return <div className="text-[13px] text-[#747474]">Loading…</div>;

  const { totals, by_stage, avg_days_in_stage, top_interviewers, vacancy_leaderboard, by_department } = board;

  return (
    <div className="flex flex-col gap-4" data-testid="hiring-scoreboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          testid="kpi-open-vacancies"
          label={t("hire.kpi_open_vacancies")}
          value={totals.vacancies_open}
          sub={`${totals.vacancies_draft} draft · ${totals.vacancies_filled} filled`}
          color="#4338ca"
        />
        <KpiCard
          testid="kpi-active-candidates"
          label={t("hire.kpi_active_candidates")}
          value={totals.candidates_active}
          sub={`${totals.candidates_total} total`}
        />
        <KpiCard
          testid="kpi-hired"
          label={t("hire.kpi_hired")}
          value={totals.hired_total}
          color="#008932"
        />
        <KpiCard
          testid="kpi-offer-acceptance"
          label={t("hire.kpi_offer_acceptance")}
          value={`${totals.offer_acceptance_rate}%`}
          color="#c19a2b"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pipeline funnel */}
        <div className="sw-card">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold mb-3">{t("hire.by_stage")}</div>
          <div className="flex flex-col gap-1.5">
            {STAGES.map((s) => {
              const count = by_stage[s.key] || 0;
              const max = Math.max(...Object.values(by_stage), 1);
              const pct = (count / max) * 100;
              return (
                <div key={s.key} className="flex items-center gap-3 text-[12px]">
                  <div className="w-24 text-[#475569] font-medium truncate">{t(s.label)}</div>
                  <div className="flex-1 h-4 bg-[#f6f6f6] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: s.color }}></div>
                  </div>
                  <div className="w-8 text-right text-[#0f172a] font-mono font-semibold">{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Avg days per stage */}
        <div className="sw-card">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold mb-3">{t("hire.avg_days_in_stage")}</div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(avg_days_in_stage).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-3 py-2 bg-[#f8fafc] rounded-[10px]">
                <span className="text-[12px] text-[#475569] capitalize">{k.replace("_", " ")}</span>
                <span className="text-[13px] font-mono font-semibold text-[#4338ca]">{v}d</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top interviewers */}
        <div className="sw-card">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold mb-3">{t("hire.top_interviewers")}</div>
          {top_interviewers.length === 0 ? (
            <div className="text-[12px] text-[#747474]">—</div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {top_interviewers.map((it, i) => (
                <div key={it.id} className="flex items-center justify-between px-3 py-2 bg-[#f8fafc] rounded-[10px]">
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="text-[#94a3b8] font-mono w-5">#{i + 1}</span>
                    <span className="text-[#0f172a] font-medium">{it.name}</span>
                  </div>
                  <span className="text-[11px] text-[#4338ca] font-mono">
                    {t("hire.approved_of_assigned", { approved: it.approved, assigned: it.assigned })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Vacancy leaderboard */}
        <div className="sw-card">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold mb-3">{t("hire.vacancy_leaderboard")}</div>
          {vacancy_leaderboard.length === 0 ? (
            <div className="text-[12px] text-[#747474]">—</div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {vacancy_leaderboard.map((it, i) => (
                <div key={it.vacancy_id} className="flex items-center justify-between px-3 py-2 bg-[#f8fafc] rounded-[10px]">
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="text-[#94a3b8] font-mono w-5">#{i + 1}</span>
                    <span className="text-[#0f172a] font-medium truncate max-w-[220px]">{it.title}</span>
                  </div>
                  <span className="text-[11px] text-[#4338ca] font-mono">{it.count} cand.</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* By department */}
        {Object.keys(by_department).length > 0 && (
          <div className="sw-card lg:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold mb-3">Open vacancies · by department</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(by_department).map(([did, cnt]) => (
                <div key={did} className="px-3 py-1.5 bg-[#eef2ff] rounded-[10px] text-[12px]">
                  <span className="text-[#4338ca] font-medium">{deptName(did)}</span>
                  <span className="text-[#94a3b8] ml-2 font-mono">{cnt}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Hiring() {
  const { t } = useI18n();
  const [tab, setTab] = useState("pipeline");
  const [vacancies, setVacancies] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobProfiles, setJobProfiles] = useState([]);
  const [accessProfiles, setAccessProfiles] = useState([]);
  const [showVacancyModal, setShowVacancyModal] = useState(false);
  const [vacancyToEdit, setVacancyToEdit] = useState(null);
  const [applicantVacancyForNew, setApplicantVacancyForNew] = useState(null); // when clicking "add candidate" from pipeline
  const [detailApplicant, setDetailApplicant] = useState(null);

  // Pipeline filters
  const [filterVacancyId, setFilterVacancyId] = useState("all");
  const [filterDepartmentId, setFilterDepartmentId] = useState("all");
  const [filterPositionId, setFilterPositionId] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [candidateSearch, setCandidateSearch] = useState("");

  // Vacancies filters
  const [vacStatusFilter, setVacStatusFilter] = useState("all");
  const [vacDepartmentFilter, setVacDepartmentFilter] = useState("all");

  const load = async () => {
    const [v, a, d, jp, ap] = await Promise.all([
      api.get("/vacancies"), api.get("/applicants"), api.get("/departments"),
      api.get("/job-profiles"), api.get("/access-profiles"),
    ]);
    setVacancies(v.data);
    setApplicants(a.data);
    setDepartments(d.data);
    setJobProfiles(jp.data);
    setAccessProfiles(ap.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const approveVacancy = async (vid) => {
    await api.post(`/vacancies/${vid}/approve`);
    await load();
  };
  const deleteVacancy = async (vid) => {
    if (!window.confirm(t("hire.delete_confirm"))) return;
    try {
      await api.delete(`/vacancies/${vid}`);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    }
  };
  const openEditVacancy = (v) => { setVacancyToEdit(v); setShowVacancyModal(true); };

  // Filtered applicants for kanban
  const filteredApplicants = useMemo(() => {
    let list = applicants;
    if (filterVacancyId !== "all") list = list.filter((a) => a.vacancy_id === filterVacancyId);
    if (filterDepartmentId !== "all") {
      const deptVacs = new Set(vacancies.filter((v) => v.department_id === filterDepartmentId).map((v) => v.id));
      list = list.filter((a) => deptVacs.has(a.vacancy_id));
    }
    if (filterPositionId !== "all") {
      const posVacs = new Set(vacancies.filter((v) => v.job_profile_id === filterPositionId).map((v) => v.id));
      list = list.filter((a) => posVacs.has(a.vacancy_id));
    }
    if (filterPriority !== "all") {
      const priVacs = new Set(vacancies.filter((v) => v.priority === filterPriority).map((v) => v.id));
      list = list.filter((a) => priVacs.has(a.vacancy_id));
    }
    if (candidateSearch.trim()) {
      const q = candidateSearch.trim().toLowerCase();
      list = list.filter((a) => (a.name || "").toLowerCase().includes(q) || (a.email || "").toLowerCase().includes(q));
    }
    return list;
  }, [applicants, vacancies, filterVacancyId, filterDepartmentId, filterPositionId, filterPriority, candidateSearch]);

  const resetPipelineFilters = () => {
    setFilterVacancyId("all"); setFilterDepartmentId("all");
    setFilterPositionId("all"); setFilterPriority("all"); setCandidateSearch("");
  };

  const filteredVacancies = useMemo(() => {
    let list = vacancies;
    if (vacStatusFilter !== "all") list = list.filter((v) => v.status === vacStatusFilter);
    if (vacDepartmentFilter !== "all") list = list.filter((v) => v.department_id === vacDepartmentFilter);
    return list;
  }, [vacancies, vacStatusFilter, vacDepartmentFilter]);

  const vacancyLookup = useMemo(() => Object.fromEntries(vacancies.map((v) => [v.id, v])), [vacancies]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><Briefcase size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("hire.title")}</h1>
            <p className="text-[14px] text-[#747474]">{vacancies.filter((v) => v.status === "open").length} vagas abertas · {applicants.length} candidatos</p>
          </div>
        </div>
        <button data-testid="new-vacancy-btn" onClick={() => { setVacancyToEdit(null); setShowVacancyModal(true); }} className="sw-btn-primary"><Plus size={14} />  {t("hire.new_vacancy")}</button>
      </div>

      <div className="inline-flex bg-white border border-[#eeeded] rounded-full p-1 w-fit">
        <button data-testid="tab-scoreboard" onClick={() => setTab("scoreboard")} className={`px-5 py-2 rounded-full text-[13px] font-medium ${tab === "scoreboard" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>{t("hire.tab_scoreboard")}</button>
        <button data-testid="tab-pipeline" onClick={() => setTab("pipeline")} className={`px-5 py-2 rounded-full text-[13px] font-medium ${tab === "pipeline" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>{t("hire.tab_pipeline")}</button>
        <button data-testid="tab-vacancies" onClick={() => setTab("vacancies")} className={`px-5 py-2 rounded-full text-[13px] font-medium ${tab === "vacancies" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>{t("hire.tab_vacancies")}</button>
      </div>

      {tab === "scoreboard" && (
        <RecruitmentScoreboard vacancies={vacancies} applicants={applicants} departments={departments} />
      )}

      {tab === "vacancies" && (
        <>
          {/* Filters row */}
          <div className="flex flex-wrap items-end gap-3 bg-white border border-[#eeeded] rounded-[16px] p-4">
            <div className="min-w-[160px]">
              <label className="sw-label !mb-1">{t("hire.filter_status")}</label>
              <select data-testid="vac-filter-status" value={vacStatusFilter} onChange={(e) => setVacStatusFilter(e.target.value)} className="sw-input">
                <option value="all">{t("hire.filter_all")}</option>
                <option value="draft">draft</option>
                <option value="open">open</option>
                <option value="filled">filled</option>
                <option value="closed">closed</option>
              </select>
            </div>
            <div className="min-w-[200px]">
              <label className="sw-label !mb-1">{t("hire.filter_department")}</label>
              <select data-testid="vac-filter-department" value={vacDepartmentFilter} onChange={(e) => setVacDepartmentFilter(e.target.value)} className="sw-input">
                <option value="all">{t("hire.filter_all")}</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="text-[12px] text-[#747474] ml-auto">
              {filteredVacancies.length} / {vacancies.length}
            </div>
          </div>

          {filteredVacancies.length === 0 ? (
            <div className="sw-card text-center text-[13px] text-[#747474] py-10">Nenhuma vaga com esses filtros.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredVacancies.map((v) => {
                const count = applicants.filter((a) => a.vacancy_id === v.id).length;
                const statusCls = v.status === "open" ? "sw-badge-approved" : v.status === "draft" ? "sw-badge-pending" : v.status === "filled" ? "sw-badge-gold" : "sw-badge-neutral";
                const ap = accessProfiles.find((p) => p.id === v.access_profile_id);
                const dept = departments.find((d) => d.id === v.department_id);
                return (
                  <div key={v.id} className="sw-card flex flex-col" data-testid={`vacancy-card-${v.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <span className={`sw-badge ${statusCls}`}>{v.status}</span>
                      <span className={`sw-badge ${priorityBadge[v.priority] || "sw-badge-neutral"}`}>{v.priority}</span>
                    </div>
                    <div className="font-medium text-[16px] text-[#323232]">{v.title}</div>
                    <div className="text-[12px] text-[#747474] mt-1 flex items-center gap-1"><MapPin size={11} /> {v.location} · {v.contract_type?.replace("_", " · ")}</div>
                    {dept && <div className="text-[12px] text-[#4338ca] mt-1"><Briefcase size={11} className="inline mr-1" />{dept.name}</div>}
                    {v.payment_components_proposed?.[0] && (
                      <div className="text-[12px] text-[#475569] mt-1 flex items-center gap-1"><DollarSign size={11} /> ${v.payment_components_proposed[0].amount} {v.payment_components_proposed[0].format}</div>
                    )}
                    {ap && <div className="text-[12px] text-[#747474] mt-1 flex items-center gap-1"><Key size={11} /> {ap.name}</div>}
                    {v.equipment && v.equipment.length > 0 && (
                      <div className="text-[12px] text-[#747474] mt-1 flex items-center gap-1"><Wrench size={11} /> {v.equipment.length} item(s)</div>
                    )}
                    {v.approval_chain && v.approval_chain.length > 0 && v.status === "draft" && (
                      <div className="mt-2 text-[11px]">
                        <div className="uppercase tracking-wider text-[#747474] mb-1">Approval chain</div>
                        <div className="flex flex-wrap items-center gap-1">
                          {v.approval_chain.map((s, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && <span className="text-[#747474]">→</span>}
                              <span className={`sw-badge !text-[10px] !py-0 ${s.status === "approved" ? "sw-badge-approved" : "sw-badge-neutral"}`}>{s.role}{s.status === "approved" ? " ✓" : ""}</span>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="text-[12px] text-[#747474] mt-2">{v.filled || 0}/{v.headcount} preenchida{v.headcount > 1 ? "s" : ""}</div>
                    {v.job_description && <p className="text-[12px] text-[#747474] mt-3 line-clamp-3">{v.job_description}</p>}
                    <div className="flex flex-wrap gap-2 mt-4">
                      <button
                        onClick={() => { setFilterVacancyId(v.id); setTab("pipeline"); }}
                        className="sw-btn-secondary !text-[12px] !px-3 !py-1.5"
                        data-testid={`view-pipeline-${v.id}`}
                      >
                        View pipeline ({count})
                      </button>
                      {v.status === "draft" && (
                        <button data-testid={`approve-vac-${v.id}`} onClick={() => approveVacancy(v.id)} className="sw-btn-primary !text-[12px] !px-3 !py-1.5">{t("common.approve")}</button>
                      )}
                      <button
                        data-testid={`edit-vac-${v.id}`}
                        onClick={() => openEditVacancy(v)}
                        className="sw-btn-secondary !text-[12px] !px-3 !py-1.5"
                        title={t("hire.edit_vacancy")}
                      >
                        {t("hire.edit_vacancy")}
                      </button>
                      <button
                        data-testid={`delete-vac-${v.id}`}
                        onClick={() => deleteVacancy(v.id)}
                        className="sw-btn-secondary !text-[12px] !px-3 !py-1.5 !text-[#881a27] !border-[#e5c1c1]"
                        title={t("hire.delete_vacancy")}
                      >
                        <Trash2 size={11} /> {t("hire.delete_vacancy")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === "pipeline" && (
        <>
          {/* Unified filters for the whole funnel */}
          <div className="flex flex-wrap items-end gap-3 bg-white border border-[#eeeded] rounded-[16px] p-4">
            <div className="min-w-[200px] flex-1">
              <label className="sw-label !mb-1">{t("hire.filter_vacancy")}</label>
              <select data-testid="filter-vacancy" value={filterVacancyId} onChange={(e) => setFilterVacancyId(e.target.value)} className="sw-input">
                <option value="all">{t("hire.filter_all")} ({vacancies.length})</option>
                {vacancies.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.title} · {v.status}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[160px]">
              <label className="sw-label !mb-1">{t("hire.filter_position")}</label>
              <select data-testid="filter-position" value={filterPositionId} onChange={(e) => setFilterPositionId(e.target.value)} className="sw-input">
                <option value="all">{t("hire.filter_all")}</option>
                {jobProfiles.map((p) => <option key={p.id} value={p.id}>{p.title || p.name}</option>)}
              </select>
            </div>
            <div className="min-w-[160px]">
              <label className="sw-label !mb-1">{t("hire.filter_department")}</label>
              <select data-testid="filter-department" value={filterDepartmentId} onChange={(e) => setFilterDepartmentId(e.target.value)} className="sw-input">
                <option value="all">{t("hire.filter_all")}</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="sw-label !mb-1">{t("hire.filter_priority")}</label>
              <select data-testid="filter-priority" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="sw-input">
                <option value="all">{t("hire.filter_all")}</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="min-w-[220px] flex-1">
              <label className="sw-label !mb-1">{t("hire.filter_search_candidate")}</label>
              <input
                data-testid="filter-candidate-search"
                value={candidateSearch}
                onChange={(e) => setCandidateSearch(e.target.value)}
                placeholder={t("hire.filter_search_candidate")}
                className="sw-input"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] text-[#747474] whitespace-nowrap" data-testid="candidates-count">
                {t("hire.candidates_shown", { shown: filteredApplicants.length, total: applicants.length })}
              </span>
              <button
                type="button"
                data-testid="reset-filters-btn"
                onClick={resetPipelineFilters}
                className="text-[11px] text-[#4338ca] hover:underline text-left"
              >
                {t("hire.reset_filters")}
              </button>
            </div>
            {filterVacancyId !== "all" && (
              <button
                data-testid="new-applicant-btn"
                onClick={() => setApplicantVacancyForNew(vacancyLookup[filterVacancyId] || null)}
                className="sw-btn-primary"
              >
                <Plus size={14} />  {t("hire.add_candidate")}
              </button>
            )}
          </div>

          {filteredApplicants.length === 0 && applicants.length > 0 && (
            <div className="sw-card text-center text-[13px] text-[#747474] py-10">{t("hire.no_candidates_filter")}</div>
          )}

          {/* Vertical funnel: each stage is a horizontal band; width narrows down the pipeline */}
          <div className="flex flex-col gap-2" data-testid="hiring-funnel">
            {STAGES.map((s, idx) => {
              const items = filteredApplicants.filter((a) => a.stage === s.key);
              // Compute funnel width: each stage narrows by ~4% down to a minimum of 62%
              const nonRejectedIdx = idx < 7 ? idx : 0;  // rejected stays full width (separate)
              const width = s.key === "rejected" ? 100 : Math.max(62, 100 - nonRejectedIdx * 5);
              const isRejected = s.key === "rejected";
              return (
                <div
                  key={s.key}
                  className={`mx-auto transition-all ${isRejected ? "opacity-70" : ""}`}
                  style={{ width: `${width}%` }}
                  data-testid={`funnel-row-${s.key}`}
                >
                  <div
                    className="rounded-[14px] border border-[#eeeded] bg-white overflow-hidden"
                    style={{ borderLeftWidth: 5, borderLeftColor: s.color }}
                  >
                    <div className="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-white to-[#f8fafc]">
                      <div className="w-2 h-2 rounded-full" style={{ background: s.color }}></div>
                      <span className="text-[12px] font-semibold text-[#0f172a] uppercase tracking-wider">{t(s.label)}</span>
                      <span
                        className="text-[10.5px] font-mono font-semibold px-2 py-0.5 rounded-full text-white"
                        style={{ background: s.color }}
                        data-testid={`funnel-count-${s.key}`}
                      >
                        {items.length}
                      </span>
                      <div className="ml-auto flex items-center gap-1 text-[11px] text-[#94a3b8]">
                        {/* thin band showing % vs top-of-funnel */}
                        {filteredApplicants.length > 0 && (
                          <span className="font-mono">
                            {Math.round((items.length / filteredApplicants.length) * 100)}%
                          </span>
                        )}
                      </div>
                    </div>
                    {items.length === 0 ? (
                      <div className="px-4 py-3 text-[11.5px] text-[#c8c8c8] italic">{t("hire.funnel_empty_stage")}</div>
                    ) : (
                      <div className="flex flex-wrap gap-2 p-3 bg-[#fafafa]">
                        {items.map((a) => {
                          const vac = vacancyLookup[a.vacancy_id];
                          return (
                            <button
                              key={a.id}
                              data-testid={`applicant-card-${a.id}`}
                              onClick={() => setDetailApplicant(a)}
                              className="bg-white rounded-[10px] px-3 py-2 text-left border border-transparent hover:border-[#475569] hover:shadow-sm transition-all min-w-[180px] max-w-[240px] flex-1"
                            >
                              <div className="font-medium text-[13px] text-[#323232] truncate">{a.name}</div>
                              {vac && (
                                <div className="text-[10px] text-[#4338ca] mt-0.5 truncate flex items-center gap-1">
                                  <Briefcase size={9} /> {vac.title}
                                </div>
                              )}
                              <div className="flex gap-1 mt-1 flex-wrap items-center">
                                {a.assigned_interviewer_name && s.key === "interview" && (
                                  <span className="text-[9px] text-[#94a3b8] truncate max-w-[120px]">
                                    → {a.assigned_interviewer_name}
                                  </span>
                                )}
                                {a.background_check_status && <span className="sw-badge sw-badge-approved !text-[9px] !px-1.5 !py-0">BG ✓</span>}
                                {a.offer && <span className="sw-badge sw-badge-gold !text-[9px] !px-1.5 !py-0">OFFER</span>}
                                {a.hired_user_id && <span className="sw-badge sw-badge-approved !text-[9px] !px-1.5 !py-0">HIRED</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {showVacancyModal && (
        <NewVacancyModal
          departments={departments}
          jobProfiles={jobProfiles}
          accessProfiles={accessProfiles}
          vacancyToEdit={vacancyToEdit}
          onClose={() => { setShowVacancyModal(false); setVacancyToEdit(null); }}
          onSaved={() => { setShowVacancyModal(false); setVacancyToEdit(null); load(); }}
        />
      )}
      {applicantVacancyForNew && (
        <NewApplicantModal
          vacancy={applicantVacancyForNew}
          onClose={() => setApplicantVacancyForNew(null)}
          onSaved={() => { setApplicantVacancyForNew(null); load(); }}
        />
      )}
      {detailApplicant && (
        <ApplicantDetailModal
          applicant={detailApplicant}
          vacancy={vacancies.find((v) => v.id === detailApplicant.vacancy_id)}
          onClose={() => setDetailApplicant(null)}
          onUpdated={async () => {
            await load();
            const fresh = (await api.get("/applicants")).data.find((x) => x.id === detailApplicant.id);
            setDetailApplicant(fresh || null);
          }}
        />
      )}
    </div>
  );
}
